import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { UserPlus, Upload, Stethoscope, Mail, User, Camera, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AddDoctor = () => {
    const { register, handleSubmit, formState: { errors }, watch } = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    const imageHostKey = "47c180edbde28084dbfe786da39d0ad7";
    const navigate = useNavigate();

    // Watch for image changes to show preview
    const watchedImage = watch("image");

    React.useEffect(() => {
        if (watchedImage && watchedImage[0]) {
            const file = watchedImage[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }, [watchedImage]);

    const { data: specialties = [], isLoading, error } = useQuery({
        queryKey: ['specialty'],
        queryFn: async () => {
            const res = await fetch('https://doctors-portal-server-ten-iota.vercel.app/appointmentSpecialty');
            if (!res.ok) {
                throw new Error('Failed to fetch specialties');
            }
            const data = await res.json();
            return data;
        }
    });

    const handleAddDoctor = async (data) => {
        setIsSubmitting(true);
        try {
            const image = data.image[0];
            const formData = new FormData();
            formData.append('image', image);
            const url = `https://api.imgbb.com/1/upload?key=${imageHostKey}`;

            // Upload image
            const imageResponse = await fetch(url, {
                method: 'POST',
                body: formData
            });

            if (!imageResponse.ok) {
                throw new Error('Failed to upload image');
            }

            const imgData = await imageResponse.json();

            if (imgData.success) {
                const doctor = {
                    name: data.name,
                    email: data.email,
                    specialty: data.specialty,
                    image: imgData.data.url
                };

                // Save doctor information to the database
                const doctorResponse = await fetch('https://doctors-portal-server-ten-iota.vercel.app/doctors', {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                        authorization: `bearer ${localStorage.getItem('accessToken')}`
                    },
                    body: JSON.stringify(doctor)
                });

                if (!doctorResponse.ok) {
                    throw new Error('Failed to add doctor');
                }

                const result = await doctorResponse.json();
                console.log(result);
                toast.success(`${result.name} has been added successfully!`);
                navigate('/dashboard/managedoctors');
            } else {
                throw new Error('Image upload failed');
            }
        } catch (error) {
            console.error('Error adding doctor:', error);
            toast.error('Failed to add doctor. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-96 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-gray-600">Loading specialties...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-96 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-center">
                    <AlertCircle className="h-12 w-12 text-red-500" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Error Loading Data</h3>
                        <p className="text-gray-600 mt-1">Failed to fetch specialties. Please try again.</p>
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-6">
                    <div className="flex items-center gap-4 mb-4">
                        <button
                            onClick={() => navigate('/dashboard/managedoctors')}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="h-5 w-5 text-gray-600" />
                        </button>
                        <div className="flex items-center gap-3">
                            <UserPlus className="h-6 w-6 text-blue-600" />
                            <h1 className="text-2xl font-bold text-gray-900">Add New Doctor</h1>
                        </div>
                    </div>
                    <p className="text-gray-600">Fill out the form below to add a new doctor to the system</p>
                </div>

                {/* Form */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <form onSubmit={handleSubmit(handleAddDoctor)} className="space-y-6">
                        {/* Name Field */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <User className="h-4 w-4" />
                                Full Name
                            </label>
                            <input
                                type="text"
                                {...register("name", {
                                    required: "Name is required",
                                    minLength: {
                                        value: 2,
                                        message: "Name must be at least 2 characters"
                                    }
                                })}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.name ? 'border-red-300' : 'border-gray-300'
                                    }`}
                                placeholder="Enter doctor's full name"
                            />
                            {errors.name && (
                                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                    <AlertCircle className="h-4 w-4" />
                                    {errors.name.message}
                                </p>
                            )}
                        </div>

                        {/* Email Field */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <Mail className="h-4 w-4" />
                                Email Address
                            </label>
                            <input
                                type="email"
                                {...register("email", {
                                    required: "Email is required",
                                    pattern: {
                                        value: /^\S+@\S+$/i,
                                        message: "Invalid email address"
                                    }
                                })}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.email ? 'border-red-300' : 'border-gray-300'
                                    }`}
                                placeholder="Enter doctor's email address"
                            />
                            {errors.email && (
                                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                    <AlertCircle className="h-4 w-4" />
                                    {errors.email.message}
                                </p>
                            )}
                        </div>

                        {/* Specialty Field */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <Stethoscope className="h-4 w-4" />
                                Medical Specialty
                            </label>
                            <select
                                {...register('specialty', {
                                    required: "Please select a specialty"
                                })}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.specialty ? 'border-red-300' : 'border-gray-300'
                                    }`}
                            >
                                <option value="">Select a specialty</option>
                                {specialties.map(specialty => (
                                    <option key={specialty._id} value={specialty.name}>
                                        {specialty.name}
                                    </option>
                                ))}
                            </select>
                            {errors.specialty && (
                                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                    <AlertCircle className="h-4 w-4" />
                                    {errors.specialty.message}
                                </p>
                            )}
                        </div>

                        {/* Image Upload Field */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <Camera className="h-4 w-4" />
                                Doctor's Photo
                            </label>

                            <div className="flex items-start gap-4">
                                <div className="flex-1">
                                    <div className="relative">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            {...register("image", {
                                                required: "Photo is required"
                                            })}
                                            className="hidden"
                                            id="image-upload"
                                        />
                                        <label
                                            htmlFor="image-upload"
                                            className={`flex items-center justify-center w-full px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${errors.image ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                        >
                                            <div className="text-center">
                                                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                                <p className="text-sm text-gray-600">
                                                    Click to upload or drag and drop
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    PNG, JPG, GIF up to 10MB
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                    {errors.image && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                            <AlertCircle className="h-4 w-4" />
                                            {errors.image.message}
                                        </p>
                                    )}
                                </div>

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className="w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => navigate('/dashboard/managedoctors')}
                                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Adding Doctor...
                                    </>
                                ) : (
                                    <>
                                        <UserPlus className="h-4 w-4" />
                                        Add Doctor
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddDoctor;